import { lazy, Suspense, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import { Footer } from './components/layout/Footer';
import { Start } from './components/pages/Start';
import { Review } from './components/pages/Review';
import { ReferenceInput } from './components/pages/ReferenceInput';
import { AIAnalysis } from './components/pages/AIAnalysis';
import { DesignSpecEditor } from './components/pages/DesignSpecEditor';
import { ProjectConfig } from './components/pages/ProjectConfig';
import { MockupPreview } from './components/pages/MockupPreview';
import { LayerOutput } from './components/pages/LayerOutput';
import { KnowledgeBrowser } from './components/pages/KnowledgeBrowser';
import { Settings } from './components/pages/Settings';
import { useProjectStore } from './store/projectStore';

// Lazy load B2B pages to keep bundle size minimal for other modes
const B2BStart = lazy(() => import('./components/pages/B2BStart'));
const B2BPreview = lazy(() => import('./components/pages/B2BPreview'));
const B2BStrategy = lazy(() => import('./components/pages/B2BStrategy'));
const B2BStream = lazy(() => import('./components/pages/B2BStream'));
const B2BDeploy = lazy(() => import('./components/pages/B2BDeploy'));
const B2BGallery = lazy(() => import('./components/pages/B2BGallery'));

function MainContent() {
  const activeTab = useProjectStore((state) => state.activeTab);

  switch (activeTab) {
    case 'start':
      return <Start />;
    case 'review':
      return <Review />;
    case 'reference':
      return <ReferenceInput />;
    case 'analysis':
      return <AIAnalysis />;
    case 'designspec':
      return <DesignSpecEditor />;
    case 'config':
      return <ProjectConfig />;
    case 'mockup':
      return <MockupPreview />;
    case 'layers':
      return <LayerOutput />;
    case 'knowledge':
      return <KnowledgeBrowser />;
    case 'settings':
      return <Settings />;
    // B2B Mode pages
    case 'b2b-start':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BStart />
        </Suspense>
      );
    case 'b2b-preview':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BPreview />
        </Suspense>
      );
    case 'b2b-strategy':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BStrategy />
        </Suspense>
      );
    case 'b2b-stream':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BStream />
        </Suspense>
      );
    case 'b2b-deploy':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BDeploy />
        </Suspense>
      );
    case 'b2b-gallery':
      return (
        <Suspense fallback={<B2BLoadingFallback />}>
          <B2BGallery />
        </Suspense>
      );
    default:
      return <Start />;
  }
}

function B2BLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      <span className="ml-3 text-slate-500">載入中...</span>
    </div>
  );
}

function App() {
  const config = useProjectStore((state) => state.config);
  const uiMode = useProjectStore((state) => state.uiMode);
  const generateLayers = useProjectStore((state) => state.generateLayers);
  const generateMockup = useProjectStore((state) => state.generateMockup);

  useEffect(() => {
    // Only auto-generate layers/mockup for expert/customer modes
    if (uiMode !== 'b2b') {
      generateLayers();
      generateMockup();
    }
  }, [config.id, uiMode]);

  useEffect(() => {
    if (uiMode === 'b2b') return;
    const timer = setTimeout(() => {
      generateLayers();
      generateMockup();
    }, 500);
    return () => clearTimeout(timer);
  }, [
    config.name,
    config.displayName,
    config.brand,
    config.contact,
    config.social,
    config.design,
    config.pages,
    config.hero,
    config.services,
    config.team,
    config.properties,
    config.news,
    config.contentBlocks,
    config.references,
    config.designSpec,
    config.tech,
    config.deploy,
    config.customVars,
  ]);

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-900">
      <Header />
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <MainContent />
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default App;
